import { titleCasePlaceName } from './titleCasePlaceName';

/**
 * Canonicalise Aquarius person names for Control Centre display.
 * Flips "SURNAME, GIVEN" → "Given Surname" and title-cases.
 */
export function formatDriverDisplayName(name: string | null | undefined): string {
  const raw = String(name || '').trim();
  if (!raw) return '';
  // Keep tachograph card ids / entity ids unchanged
  if (/^DB\d{6,}$/i.test(raw) || /^[0-9A-HJKMNP-TV-Z]{26}$/i.test(raw)) return raw;
  let display = raw;
  if (display.includes(',') && !/\b(ltd|limited|llc|inc|plc|co)\b/i.test(display)) {
    const idx = display.indexOf(',');
    const before = display.slice(0, idx).trim();
    const after = display.slice(idx + 1).trim();
    if (before && after) display = `${after} ${before}`;
  }
  return titleCasePlaceName(display);
}

/** Strip "Examples:" / "Vehicles:" prefixes. Title-case driver names. Show all when ≤ 5. */
export function formatDriverExamplesSummary(
  summary: string | null | undefined,
  kind?: 'drivers' | 'vehicles',
): string {
  const raw = String(summary || '').trim();
  if (!raw) return '';
  const match = raw.match(/^(Examples|Vehicles):\s*(.+)$/i);
  const inferredKind: 'drivers' | 'vehicles' | undefined =
    kind ||
    (match?.[1].toLowerCase() === 'vehicles' ? 'vehicles' : match ? 'drivers' : undefined);
  const rest = (match ? match[2] : raw).trim();
  if (!inferredKind) return raw;
  if (!match && /[.?!]/.test(rest)) return raw;
  if (!match && /^(no |review |based |fleet |drivers |walkaround |confirm |includes )/i.test(rest)) return raw;

  // Prefer an unambiguous separator when the API sends "First Last · First Last".
  const byDot = rest.split(/\s·\s/).map((part) => part.trim()).filter(Boolean);
  const parts = byDot.length > 1
    ? byDot
    : rest.split(',').map((part) => part.trim()).filter(Boolean);
  const hasMore = parts.some((p) => /^and more\.?\.?\.?$/i.test(p));
  const items = parts.filter((p) => !/^and more\.?\.?\.?$/i.test(p));

  let formatted: string[];
  if (inferredKind === 'vehicles') {
    // UK plates display uppercase — never run driver title-case on regs
    // (that yields "Bv16uye" / "Bu74 Ypr" from mixed-case source data).
    formatted = items.map((p) => p.replace(/\s+/g, ' ').trim().toUpperCase()).filter(Boolean);
  } else if (byDot.length > 1) {
    formatted = items.map((part) => formatDriverDisplayName(part) || part).filter(Boolean);
  } else {
    // Aquarius "SURNAME, GIVEN" lists split on commas into pairs — reassemble before title-case.
    const singleToken = items.filter((p) => !p.includes(' ')).length;
    if (items.length >= 2 && items.length % 2 === 0 && singleToken >= items.length * 0.5) {
      formatted = [];
      for (let i = 0; i < items.length; i += 2) {
        formatted.push(
          formatDriverDisplayName(`${items[i]}, ${items[i + 1]}`) || `${items[i]} ${items[i + 1]}`,
        );
      }
    } else {
      formatted = items.map((part) => formatDriverDisplayName(part) || part).filter(Boolean);
    }
  }

  const shown = formatted.length <= 5 ? formatted : formatted.slice(0, 5);
  const more = hasMore || formatted.length > 5;
  return `${shown.join(', ')}${more ? ', and more...' : ''}`;
}
